import { i18n } from "@/plugins/i18n";
import {
  atLeastOneIsRequired as cuiAtLeastOneIsRequired,
  emailRule as cuiEmailRule,
  onlyInteger as cuiOnlyInteger,
  Patterns,
  required as cuiRequired,
} from "@coronet/cui";

/**
 * Validates that the array contains at least one item.
 * @param errorMessage
 */
export const atLeastOneIsRequired = (
  errorMessage: string = i18n.global.t("validations.required")
) => cuiAtLeastOneIsRequired(errorMessage);
/**
 * Validates that the value is non-empty and not only whitespace.
 * @param errorMessage
 */
export const required = (errorMessage: string = i18n.global.t("validations.required")) =>
  cuiRequired(errorMessage);

/**
 * Builds a rule that accepts a single value or an array and fails when any value is invalid.
 * @param isInvalid
 * @param errorMessage
 */
const validateValueOrArray = (isInvalid: (value: string) => boolean, errorMessage: string) => {
  return (value: string | string[]) => {
    const values = Array.isArray(value) ? value : [value];
    return values.some(isInvalid) ? errorMessage : true;
  };
};

/**
 * Validates that the value (or each value in the array) is a valid email and not in a disallowed domain zone.
 * @param errorMessage
 */
export const emailRule = (errorMessage: string = i18n.global.t("validations.email")) =>
  cuiEmailRule(errorMessage);

/**
 * Validates that the string length does not exceed the given character count.
 * @param characterCount
 * @param errorMessage
 */
export const maxCharactersRule = (
  characterCount: number = 25,
  errorMessage: string = i18n.global.t("validations.maxNCharacters", { n: characterCount })
) => {
  return (value: string) => {
    if (value.length > characterCount) {
      return errorMessage;
    }
    return true;
  };
};

/**
 * Validates that the array length does not exceed the given items count.
 * @param maxItemsCount
 * @param errorMessage
 */
export const maxItems = (
  maxItemsCount: number = 5,
  errorMessage: string = i18n.global.t("validations.maxNItems", { n: maxItemsCount })
) => {
  return (value: unknown[]) => {
    if (value && value.length > maxItemsCount) {
      return errorMessage;
    }
    return true;
  };
};

/**
 * Validates that the value is greater than or equal to the given minimum.
 * @param minNumber
 * @param errorMessage
 */
export const minNumber = (
  minNumber: number = 1,
  errorMessage: string = i18n.global.t("validations.minNumberAllowed", { n: minNumber })
) => {
  return (value: number) => {
    if (value < minNumber) {
      return errorMessage;
    }
    return true;
  };
};

/**
 * Validates that the value is less than or equal to the given maximum.
 * @param maxNumber
 * @param errorMessage
 */
export const maxNumber = (
  maxNumber: number = 1,
  errorMessage: string = i18n.global.t("validations.maxNumberAllowed", { n: maxNumber })
) => {
  return (value: number) => {
    if (value > maxNumber) {
      return errorMessage;
    }
    return true;
  };
};

/**
 * Validates that the numeric value is provided and non-negative.
 * @param errorMessage
 */
export const numberRequired = (errorMessage: string = i18n.global.t("validations.required")) => {
  return (value: number) => {
    if (!value || value < 0) return errorMessage;
    return true;
  };
};

/**
 * Validates that the value contains only digits (no decimals or non-numeric characters).
 * @param integerErrorMessage
 */
export const onlyInteger = (
  integerErrorMessage: string = i18n.global.t("validations.onlyInteger")
) => cuiOnlyInteger(integerErrorMessage);

/**
 * Validates that the value has no more than the given number of decimal places.
 * @param maxNumber
 * @param maxNDecimalPointsErrorMessage
 */
export const maxNDecimalPoints = (
  maxNumber: number = 2,
  maxNDecimalPointsErrorMessage: string = i18n.global.t("validations.maxNDecimalPoints", {
    n: maxNumber,
  })
) => {
  return (value: number) => {
    const valueAsString = value.toString();
    const [, decimals] = valueAsString.split(".");
    if (decimals) {
      if (decimals.length > maxNumber) {
        return maxNDecimalPointsErrorMessage;
      }
    }
    return true;
  };
};

/**
 * Validates that the value (or each value in the array) contains no special characters.
 * @param errorMessage
 */
export const noSpecialCharacters = (
  errorMessage: string = i18n.global.t("validations.noSpecialCharacters")
) => validateValueOrArray((v) => !Patterns.NO_SPECIAL_CHARACTERS.test(v), errorMessage);

/**
 * Validates that the value (or each value in the array) contains only letters, numbers, and spaces.
 * @param errorMessage
 */
export const lettersNumbersAndSpace = (
  errorMessage: string = i18n.global.t("validations.lettersNumbersAndSpace")
) => validateValueOrArray((v) => !Patterns.LETTERS_NUMBERS_SPACE.test(v), errorMessage);

const RESERVED_IPV4_RANGES = [
  { network: "0.0.0.0", prefix: 8 }, // "This" network
  { network: "10.0.0.0", prefix: 8 }, // RFC 1918 private
  { network: "100.64.0.0", prefix: 10 }, // Carrier-grade NAT
  { network: "127.0.0.0", prefix: 8 }, // Loopback
  { network: "169.254.0.0", prefix: 16 }, // Link-local
  { network: "172.16.0.0", prefix: 12 }, // RFC 1918 private
  { network: "192.0.2.0", prefix: 24 }, // TEST-NET-1
  { network: "192.168.0.0", prefix: 16 }, // RFC 1918 private
  { network: "198.18.0.0", prefix: 15 }, // Benchmarking
  { network: "198.51.100.0", prefix: 24 }, // TEST-NET-2
  { network: "203.0.113.0", prefix: 24 }, // TEST-NET-3
  { network: "224.0.0.0", prefix: 4 }, // Multicast
  { network: "240.0.0.0", prefix: 4 }, // Reserved / Class E
  { network: "255.255.255.255", prefix: 32 }, // Limited broadcast
];

const RESERVED_IPV6_RANGES = [
  { network: "::1", prefix: 128 }, // Loopback
  { network: "fc00::", prefix: 7 }, // Unique local
  { network: "fe80::", prefix: 10 }, // Link-local
  { network: "fec0::", prefix: 10 }, // Site-local (deprecated)
  { network: "ff00::", prefix: 8 }, // Multicast
  { network: "100::", prefix: 64 }, // Discard-only
  { network: "2001:2::", prefix: 48 }, // Benchmarking
  { network: "2001:db8::", prefix: 32 }, // Documentation
];

const IPV4_MAPPED_IPV6 =
  /^(?:::|(?:0{1,4}:){5})[fF]{4}:((?:\d{1,3}\.){3}\d{1,3})(?:\/(12[0-8]|1[01]\d|\d{1,2}))?$/;
const IPV4_STRICT =
  /^((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]?|0)\.){3}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9][0-9]?|0)$/;

// Packs the four octets into one 32-bit integer. `>>> 0` reinterprets the result as unsigned,
// since `<<` works on signed int32 and would turn addresses from 128.0.0.0 up negative.
function ipv4ToInt(ip: string): number {
  return ip.split(".").reduce((int, octet) => ((int << 8) | parseInt(octet, 10)) >>> 0, 0);
}

function isReservedIPv4(ip: string): boolean {
  const ipInt = ipv4ToInt(ip);
  return RESERVED_IPV4_RANGES.some(({ network, prefix }) => {
    // `(1 << (32 - prefix)) - 1` is the host part of the address; its complement keeps the top
    // `prefix` bits, the network part. Two addresses share a block when those bits match.
    const mask = ~((1 << (32 - prefix)) - 1) >>> 0;
    return (ipInt & mask) === (ipv4ToInt(network) & mask);
  });
}

// Expands `::` into the zero groups it stands for so every address becomes eight 16-bit groups,
// e.g. `fe80::1` -> [0xfe80, 0, 0, 0, 0, 0, 0, 1].
function expandIPv6(ip: string): number[] {
  const halves = ip.split("::");
  if (halves.length === 2) {
    const left = halves[0] ? halves[0].split(":").map((g) => parseInt(g, 16)) : [];
    const right = halves[1] ? halves[1].split(":").map((g) => parseInt(g, 16)) : [];
    const fill = Array<number>(8 - left.length - right.length).fill(0);
    return [...left, ...fill, ...right];
  }
  return ip.split(":").map((g) => parseInt(g, 16));
}

function isInIPv6CIDR(ip: string, network: string, prefix: number): boolean {
  const ipGroups = expandIPv6(ip);
  const netGroups = expandIPv6(network);
  // Same idea as the IPv4 mask, applied one 16-bit group at a time until `prefix` bits are used.
  let bitsLeft = prefix;
  for (let i = 0; i < 8 && bitsLeft > 0; i++) {
    const bits = Math.min(bitsLeft, 16);
    const mask = bits === 16 ? 0xffff : ~((1 << (16 - bits)) - 1) & 0xffff;
    if ((ipGroups[i] & mask) !== (netGroups[i] & mask)) return false;
    bitsLeft -= 16;
  }
  return true;
}

function isReservedIPv6(ip: string): boolean {
  const normalized = ip.toLowerCase();
  return RESERVED_IPV6_RANGES.some(({ network, prefix }) =>
    isInIPv6CIDR(normalized, network, prefix)
  );
}

export function getIpv4MappedAddress(value: string): string | null {
  const match = IPV4_MAPPED_IPV6.exec(value.trim());
  if (!match) return null;

  return IPV4_STRICT.test(match[1]) ? match[1] : null;
}

export function isReservedIP(value: string): boolean {
  const ip = value.split("/")[0];
  // Mapped addresses are checked against the IPv4 ranges, and are intercepted
  // here because expandIPv6 reads their trailing dotted quad as hex.
  const mappedIpv4 = getIpv4MappedAddress(ip);
  if (mappedIpv4) {
    return isReservedIPv4(mappedIpv4);
  }
  return ip.includes(":") ? isReservedIPv6(ip) : isReservedIPv4(ip);
}
